import { Component, Input, Output, EventEmitter, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { UniqueIDService } from '../../../../Common/Misc/services/unique-id.service';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { BrowseImageComponentText } from './browse-image-component-text';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { ErrorTypeEnum } from '../../enums/error-type.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { BehaviorSubject } from 'rxjs';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MessageService } from 'primeng/api';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';

export const contactConstants: any = {
    imageBase64Converter: 'data:image/jpeg;base64,',
    images: '/images/'
};

@Component({
    selector: 'agpr-browse-image',
    templateUrl: './browse-image.component.html',
    styleUrls: ['./browse-image.component.scss'],
    standalone: false
})
export class BrowseImageComponent implements OnChanges {
    readonly buttonTypes = ButtonTypesEnum;
    @Input() showPlaceholder: boolean = false;
    @Input() imageUrl: string = '';
    @Input() mainClass: string = '';
    @Input() imageClass: string = '';
    @Output() getImageFile: EventEmitter<File> = new EventEmitter<File>();
    @Output() getImageUrl: EventEmitter<string> = new EventEmitter<string>();
    @Output() getImageError: EventEmitter<string> = new EventEmitter<string>();
    @Output() doDelete: EventEmitter<any> = new EventEmitter<boolean>();
    @Output() moveUp: EventEmitter<any> = new EventEmitter<boolean>();
    @Output() moveDown: EventEmitter<any> = new EventEmitter<boolean>();
    id: number = null;
    @Input() showImageHtml: boolean;
    @Input() showImageInput: boolean;
    @Input() placeholderWidth: string;
    @Input() placeholderHeight: string;
    @Input() isContact: boolean;
    @Input() placeholderName: string;
    @Input() imageIcon: string;
    public uploadMaxSize: number;
    public imageLocalization = new BrowseImageComponentText();
    public imageTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/tiff', 'image/gif'];
    @Input() showDeleteButton: boolean;
    @Input() showMoveButtons: boolean = false;
    @Input() moveUpDisabled: boolean = false;
    @Input() moveDownDisabled: boolean = false;
    @Input() borderClass: string = '';
    @Input() placeholderClass: string = '';
    @Input() isDisabled: boolean = false;
    @Input() public isImageDirty: boolean = false;
    @Output() public isImageDirtyChange: EventEmitter<boolean> = new EventEmitter();
    @Input() imageId: string = '';
    @Input() ariaLabel: string;
    public filename: string = '';
    files: any[] = [];
    overlayContainer: Element;

    @ViewChild('contentLibraryTrigger') contentLibraryTrigger: ElementRef;

    constructor(
        private uniqueIDService: UniqueIDService,
        private sanitizer: DomSanitizer,
        private configurationService: ConfigService,
        private coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {
        this.id = uniqueIDService.generateID();

        if (!this.placeholderName) {
            this.placeholderName = 'Image';
        }

        if (!this.imageIcon) {
            this.imageIcon = 'fa-regular fa-duotone fa-image';
        }

        // maximum size of the Image that can be uploaded in byte
        this.uploadMaxSize = 1048576;
    }

    ngOnChanges() {
        if ((this.imageUrl !== '' && this.imageUrl != null) ||
            (this.imageId !== '' && this.imageId != null)) {
            this.showImageHtml = true;
            this.showImageInput = false;
        } else {
            this.showImageHtml = false;
            this.showImageInput = true;
        }
    }

    public resetInputValue(input: any): void {
        input.value = '';
    }

    public handleFileInput(file: File, input: any): void {
        if (file) {
            if (this.imageTypes.indexOf(file.type) < 0) {
                this.emitImageError(ErrorTypeEnum.ImageFileType);
                this.filename = '';
                input.value = '';
            } else if (file.size > this.uploadMaxSize) {
                this.filename = '';
                input.value = '';
                this.emitImageError(ErrorTypeEnum.ImagefileSize);
            } else {
                this.emitImageFile(file);
                this.filename = file.name;
                const reader = new FileReader();
                reader.onloadend = (events: any) => {
                    this.showImageHtml = true;
                    this.showImageInput = false;
                    const [, data] = events.target.result.split(':', 2);
                    const [contentType, encoded] = data.split(';', 2);
                    const [encoding, encodedData] = encoded.split(',', 2);
                    this.imageUrl = encodedData;
                    this.emitImageUrl(this.imageUrl);
                    this.isImageDirtyChange.emit(true);
                };
                reader.readAsDataURL(file);
            }
        }
    }

    openContentLibrary(): void {
        if (this.isDisabled) {
            return;
        }
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );


        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                selectionMode: true,
                useAcceptedFilesTypes: true,
                singleSelect: true,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles?.length) {
                        this.handleContentLibrarySelection(selectedFiles[0]);
                    }

                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    private handleContentLibrarySelection(file: MediaSummaryModel): void {
        if (!file) {
            return;
        }
        this.showImageHtml = true;
        this.showImageInput = false;
        this.imageId = '';
        this.filename = file.name;
        this.imageUrl = file.url;
        this.emitImageUrl(file.url);
        this.isImageDirtyChange.emit(true);
    }


    emitImageUrl(url: string): void {
        this.getImageUrl.emit(url);
    }

    emitImageError(errorType: string): void {
        this.getImageError.emit(errorType);
    }

    public getImageSrc(imageUrl: string, imageId: string): SafeUrl {
        if (imageUrl?.startsWith('http') || imageUrl?.startsWith('https')) {
            return this.sanitizer.bypassSecurityTrustUrl(imageUrl);
        }

        if (imageUrl != null && imageUrl !== '') {
            const imageBase64 = contactConstants.imageBase64Converter + imageUrl;
            return this.sanitizer.bypassSecurityTrustUrl(imageBase64);
        }

        if (imageId != null && imageId !== '') {
            return this.sanitizer.bypassSecurityTrustUrl(
                this.configurationService.getConfig().apiUrl + contactConstants.images + imageId
            );
        }

        return '';
    }

    public onImageDelete(): void {
        if (this.isDisabled) {
            return;
        }
        if (this.imageId !== '') {
            this.isImageDirtyChange.emit(true);
        }
        this.showImageHtml = false;
        this.showImageInput = true;
        this.imageUrl = '';
        this.emitImageUrl(this.imageUrl);
        this.emitImageFile(null);
        this.doDelete.emit(this);
    }

    emitImageFile(file: File): void {
        this.getImageFile.emit(file);
    }

    public convertBase64ToImage(image: string): SafeUrl {
        if (image != null && image !== '') {
            const imageBase64 = contactConstants.imageBase64Converter + image;
            return this.sanitizer.bypassSecurityTrustUrl(imageBase64);
        }
        return '';
    }

    public getImageUrlBasedOnImageID(image: string): string {
        if (image != null && image !== '') {
            return this.configurationService.getConfig().apiUrl + contactConstants.images + image;
        }
        return '';
    }

    public onMoveUp(): void {
        this.moveUp.emit();
    }
    public onMoveDown(): void {
        this.moveDown.emit();
    }
}
